//#region src/api/internal/path.ts
/**
* Extract parameter names from a path pattern at runtime.
* Examples:
* - "/users/:id" => ["id"]
* - "/files/**" => ["**"]
* - "/files/**:rest" => ["rest"]
*/
function extractPathParams(pathPattern) {
	const segments = pathPattern.split("/").filter((s) => s.length > 0);
	const names = [];
	for (const segment of segments) {
		if (segment.startsWith(":")) {
			names.push(segment.slice(1));
			continue;
		}
		if (segment === "**") {
			names.push("**");
			continue;
		}
		if (segment.startsWith("**:")) {
			names.push(segment.slice(3));
			continue;
		}
	}
	return names;
}
/**
* Build a concrete path by replacing placeholders in a path pattern with values.
*
* Supports the same placeholder syntax as the matcher:
* - Named parameter ":name" is URL-encoded as a single segment
* - Anonymous wildcard "**" inserts the remainder as-is (slashes preserved)
* - Named wildcard "**:name" inserts the remainder from the named key
*
* Examples:
* - buildPath("/users/:id", { id: "123" }) => "/users/123"
* - buildPath("/files/**", { "**": "a/b" }) => "/files/a/b"
* - buildPath("/files/**:rest", { rest: "a/b" }) => "/files/a/b"
*/
function buildPath(pathPattern, params) {
	const patternSegments = pathPattern.split("/");
	const builtSegments = [];
	for (const segment of patternSegments) {
		if (segment.length === 0) {
			builtSegments.push("");
			continue;
		}
		if (segment.startsWith(":")) {
			const name = segment.slice(1);
			const value = params[name];
			if (value === void 0) throw new Error(`Missing value for path parameter :${name}`);
			builtSegments.push(encodeURIComponent(value));
			continue;
		}
		if (segment === "**") {
			const value = params["**"];
			if (value === void 0) throw new Error("Missing value for path wildcard **");
			builtSegments.push(value);
			continue;
		}
		if (segment.startsWith("**:")) {
			const name = segment.slice(3);
			const value = params[name];
			if (value === void 0) throw new Error(`Missing value for path wildcard **:${name}`);
			builtSegments.push(value);
			continue;
		}
		builtSegments.push(segment);
	}
	return builtSegments.join("/");
}

//#endregion
export { buildPath, extractPathParams };
//# sourceMappingURL=path.js.map